import { Vector2 } from '../utils/Vector2';
import { Tile, TileGrid } from '../types/GameTypes';
import { GAME_CONSTANTS } from '../utils/Constants';

interface PathNode {
  x: number;
  y: number;
  g: number; // Cost from start
  h: number; // Heuristic to goal
  f: number; // Total cost
  parent: PathNode | null;
}

export class Pathfinder {
  private pathCache: Map<string, Vector2[]> = new Map();

  /**
   * Find path using A* algorithm
   * @param ignoreBuildings - If true, ghost/flying enemies can pass through buildings
   */
  findPath(start: Vector2, goal: Vector2, grid: TileGrid, ignoreBuildings: boolean = false): Vector2[] {
    // Include ignoreBuildings in cache key to avoid incorrect cache hits
    const cacheKey = `${start.x},${start.y}-${goal.x},${goal.y}-${ignoreBuildings}`;
    if (this.pathCache.has(cacheKey)) {
      return this.pathCache.get(cacheKey)!;
    }

    const openSet: PathNode[] = [];
    const closedSet: Set<string> = new Set();
    const nodeMap: Map<string, PathNode> = new Map();

    const startNode: PathNode = {
      x: start.x,
      y: start.y,
      g: 0,
      h: this.manhattanDistance(start, goal),
      f: this.manhattanDistance(start, goal),
      parent: null,
    };

    openSet.push(startNode);
    nodeMap.set(`${start.x},${start.y}`, startNode);

    while (openSet.length > 0) {
      // Find node with lowest f score
      let currentIndex = 0;
      for (let i = 1; i < openSet.length; i++) {
        if (openSet[i].f < openSet[currentIndex].f) {
          currentIndex = i;
        }
      }

      const current = openSet.splice(currentIndex, 1)[0];
      const currentKey = `${current.x},${current.y}`;
      closedSet.add(currentKey);

      // Check if we reached the goal
      if (current.x === goal.x && current.y === goal.y) {
        const path = this.reconstructPath(current);
        this.pathCache.set(cacheKey, path);
        return path;
      }

      // Check neighbors (4-directional)
      const neighbors = [
        { x: current.x + 1, y: current.y },
        { x: current.x - 1, y: current.y },
        { x: current.x, y: current.y + 1 },
        { x: current.x, y: current.y - 1 },
      ];

      for (const neighbor of neighbors) {
        const neighborKey = `${neighbor.x},${neighbor.y}`;

        // Check bounds
        if (
          neighbor.x < 0 ||
          neighbor.y < 0 ||
          neighbor.y >= grid.length ||
          neighbor.x >= grid[neighbor.y].length
        ) {
          continue;
        }

        // Check if already processed
        if (closedSet.has(neighborKey)) {
          continue;
        }

        // Check if tile is walkable
        // Both walls and towers block paths (buildings), unless ignoreBuildings is true
        const tile = grid[neighbor.y][neighbor.x];
        if (!ignoreBuildings && (tile.type === 'wall' || tile.type === 'tower' || tile.type === 'border')) {
          continue;
        }
        // Still block borders even for ghost/flying (unless ignoreBuildings is true, then allow borders too)
        if (tile.type === 'border' && !ignoreBuildings) {
          continue;
        }

        // Calculate g score
        const g = current.g + 1;

        // Check if we've seen this node before
        let neighborNode = nodeMap.get(neighborKey);
        if (!neighborNode) {
          neighborNode = {
            x: neighbor.x,
            y: neighbor.y,
            g: g,
            h: this.manhattanDistance(new Vector2(neighbor.x, neighbor.y), goal),
            f: 0,
            parent: current,
          };
          neighborNode.f = neighborNode.g + neighborNode.h;
          nodeMap.set(neighborKey, neighborNode);
          openSet.push(neighborNode);
        } else if (g < neighborNode.g) {
          // Found a better path
          neighborNode.g = g;
          neighborNode.f = neighborNode.g + neighborNode.h;
          neighborNode.parent = current;
        }
      }
    }

    // No path found
    return [];
  }

  /**
   * Find midpoint route for flying/ghost units when spawn-goal distance < 12
   */
  findMidpointRoute(start: Vector2, goal: Vector2, grid: TileGrid): Vector2[] {
    const distance = this.manhattanDistance(start, goal);
    
    if (distance >= GAME_CONSTANTS.MIN_DISTANCE) {
      // Direct route
      return [start, goal];
    }

    // Calculate arena center (midpoint)
    const centerX = Math.floor((grid[0].length) / 2);
    const centerY = Math.floor((grid.length) / 2);
    const midpoint = new Vector2(centerX, centerY);

    // Route: start -> midpoint -> goal
    return [start, midpoint, goal];
  }

  /**
   * Check if a valid path exists
   */
  checkPathExists(start: Vector2, goal: Vector2, grid: TileGrid): boolean {
    const path = this.findPath(start, goal, grid);
    return path.length > 0;
  }

  /**
   * Get target for block breaker mode (nearest blocking wall)
   * @deprecated Use getAllBlockingBuildings() instead
   */
  getBlockBreakerTarget(enemyPos: Vector2, goal: Vector2, grid: TileGrid): Vector2 | null {
    // Find nearest wall tile that's between enemy and goal
    let nearestWall: Vector2 | null = null;
    let minDistance = Infinity;

    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[y].length; x++) {
        if (grid[y][x].type === 'wall' || grid[y][x].type === 'tower') {
          const wallPos = new Vector2(x, y);
          const distToEnemy = this.manhattanDistance(enemyPos, wallPos);
          const distToGoal = this.manhattanDistance(wallPos, goal);
          
          // Prefer walls closer to enemy and in direction of goal
          if (distToEnemy < minDistance && distToGoal < this.manhattanDistance(enemyPos, goal)) {
            minDistance = distToEnemy;
            nearestWall = wallPos;
          }
        }
      }
    }

    return nearestWall;
  }

  /**
   * Find ALL buildings that are blocking the path from spawn to goal
   * Returns array of tile coordinates for all blocking buildings
   * Uses an optimized approach: checks buildings in a corridor between spawn and goal
   */
  getAllBlockingBuildings(spawn: Vector2, goal: Vector2, grid: TileGrid): Vector2[] {
    const blockingBuildings: Vector2[] = [];
    
    // First, try to find a path
    const path = this.findPath(spawn, goal, grid);
    
    if (path.length > 0) {
      return []; // Path exists, no blocking buildings
    }
    
    // Path is blocked - find all buildings that, if removed, would allow a path
    // Strategy: Check buildings in a corridor between spawn and goal
    const minX = Math.min(spawn.x, goal.x);
    const maxX = Math.max(spawn.x, goal.x);
    const minY = Math.min(spawn.y, goal.y);
    const maxY = Math.max(spawn.y, goal.y);
    
    // Expand corridor slightly to catch diagonal blocks
    const corridorPadding = 3;
    
    for (let y = Math.max(0, minY - corridorPadding); 
         y <= Math.min(grid.length - 1, maxY + corridorPadding); 
         y++) {
      for (let x = Math.max(0, minX - corridorPadding); 
           x <= Math.min(grid[y].length - 1, maxX + corridorPadding); 
           x++) {
        const tile = grid[y][x];
        if (tile.type === 'wall' || tile.type === 'tower') {
          // Temporarily remove this building and test if path exists
          const originalType = tile.type;
          tile.type = 'empty';
          
          // CRITICAL: Clear cache before testing to prevent cache pollution
          this.clearCache();
          
          const testPath = this.findPath(spawn, goal, grid);
          if (testPath.length > 0) {
            // Path exists without this building - this building is blocking!
            blockingBuildings.push(new Vector2(x, y));
          }
          
          // Restore building
          tile.type = originalType;
          
          // CRITICAL: Clear cache again after restoring to prevent stale cache
          this.clearCache();
        }
      }
    }
    
    return blockingBuildings;
  }

  /**
   * Clear path cache (call when maze changes)
   */
  clearCache(): void {
    this.pathCache.clear();
  }

  private reconstructPath(node: PathNode): Vector2[] {
    const path: Vector2[] = [];
    let current: PathNode | null = node;

    while (current) {
      path.unshift(new Vector2(current.x, current.y));
      current = current.parent;
    }

    return path;
  }

  private manhattanDistance(a: Vector2, b: Vector2): number {
    return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  }
}

